import React, {useContext} from "react";
import {createUseStyles, useTheme} from "react-jss";
import LineChart, {LineChartData} from "./LineChart";
import {EngageAppContext} from "../../App";
import DisplayText, {TextVariant} from "engage-client/components/DisplayText";
import {HostStreak} from "./Streak";
import {HostAverageTime} from "./AverageTime";
import HostPlayerDisplay from "./HostPlayerDisplay";
import {GetLineChartData} from "./Dashboard";
import HostViewAll, {ViewAllType} from "./HostViewAll";
import {Theme} from "engage-client/theme";

const useStyles = createUseStyles<Theme>((theme) => ({
    dashboard: {
        width: "100%",
        flexGrow: 1,
        flexShrink: 0,
        background: theme.accessoryPanel.background,
        display:"flex",
        [theme.breakpoints.up("lg")] : {
            overflowY: "auto"
        }
    },
    horizontalPanel: {
        display:"flex",
        gap: 10,
        alignItems: "flex-start",
        flexGrow: 1,
        flexShrink: 0,
        [theme.breakpoints.down("md")] : {
            flexDirection: "column"
        }
    },
    verticalPanel: {
        display: "flex",
        flexDirection: "column",
        gap: 10,
        width: "100%",
        padding: 16,
        background: theme.accessoryPanel.dashBoard.background,
    },
    largePanel: {
        flexGrow: 2,
        gap: "5px",
        flexShrink: 1,
    },
    sidebar: {
        background: theme.gameComplete.background,
        width: 87,
        height: "100%",
    },
    label: {
        left: 20,
        top: 250,
        position: "relative",
        whiteSpace: "nowrap",
        writingMode: "vertical-rl",
        transform: "rotate(-180deg) translate(0, 50%)",
        opacity: 1,
        color: theme.gameComplete.thankYouTextColor,
        zIndex: 6
    },
    maxCorrect: {
        padding: 32,
        flexGrow: 1,
        background: theme.accessoryPanel.dashBoard.maxCorrect.background,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        [theme.breakpoints.only("xs")] : {
            padding: 20
        },
        width: "33.33%"
    },
    playerNames: {
        display: "flex",
        flexFlow: "column wrap",
        alignItems: "flex-start",
        width: "100%"
    },
    title: {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start"
    },
    displayTitle: {
        fontWeight: theme.accessoryPanel.dashBoard.simpleDashboard ? 700 : 400,
        color: theme.accessoryPanel.dashBoard.sectionTitleColor,
        minHeight: 36
    },
    simpleCorrect: {
        color: theme.accessoryPanel.dashBoard.averageTime.player.timeColor,
        position: "relative",
        width: 150,
        height: 157,
        [theme.breakpoints.down("md")] : {
            width: 134,
            height: 140,
        },
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        marginBottom: 20,
    },
    mostCorrectColor: {
        color: theme.accessoryPanel.dashBoard.maxCorrect.highlightColor
    }
}));

export type HostDashboardProps = {
    leaderboardRowCount: number
    sharedDisplay: boolean
}

export default function HostDashboard(props:HostDashboardProps) {
    const {appState, user, lobby} = useContext(EngageAppContext.current);
    const lineChartColumns = appState.common.totalQuestions;
    const dashboard = appState.host.dashboard;
    const common = appState.common;

    const classes = useStyles();
    const theme:Theme = useTheme();

    const lineChartData:LineChartData[] = GetLineChartData(appState, lobby, user,props.leaderboardRowCount);
    
    return (
        <div className={classes.dashboard}>
            <div className={classes.sidebar}>
                <DisplayText variant={TextVariant.s34reg}className={classes.label}>THANK YOU FOR {props.sharedDisplay ?"PLAYING":"HOSTING"}.</DisplayText>
            </div>
            <div className={classes.verticalPanel}>
                <LineChart className={classes.largePanel} data={lineChartData} totalQuestions={lineChartColumns} host={true}/>
                <div className={classes.horizontalPanel}>
                    <div className={classes.maxCorrect}>
                        <div className={classes.simpleCorrect}>
                            <DisplayText variant={TextVariant.s60med}><span className={classes.mostCorrectColor}>{dashboard.mostCorrect}</span> / {common.totalQuestions}</DisplayText>
                        </div>
                        <div className={classes.title}>
                            <DisplayText variant={TextVariant.s18reg} className={classes.displayTitle}>MOST CORRECT ANSWERS</DisplayText>
                        </div>
                        <div className={classes.playerNames}>
                            {dashboard.mostCorrectUsers.slice(0,5).map((name, index)=><HostPlayerDisplay name={name} key={index}/>)}
                            <HostViewAll number={dashboard.mostCorrect} names={dashboard.mostCorrectUsers} maxToShow={5} type={ViewAllType.Answers}/>
                        </div>
                    </div>
                    <HostStreak streak={dashboard.longestStreak} names={dashboard.longestStreakUsers} maxToShow={5} />
                    <HostAverageTime averageTimeMS={dashboard.fastestTime}  names={dashboard.fastestTimeUsers} maxToShow={3}/>
                </div>
            </div>
        </div>
    );
}